// Los avisos en el teléfono de la campaña del 25 de octubre (decisión de
// Sebastián del 20-oct-2026: dos al día, del 21 al 25). Planificación PURA,
// como lib/notificacionesPlan.js: aquí se decide qué dice cada aviso y cuándo
// llega; lib/notifications.js solo se lo entrega al sistema operativo.
//
// Reglas que el archivo sostiene y sus pruebas fijan:
//   - Dos momentos al día (media mañana y tarde) y en vez del tip de esos
//     días, no además: con la campaña, la persona recibe dos avisos al día y
//     no tres.
//   - Como mucho DOS esperando en la cola del sistema (la avalancha del
//     2026-09-04, ver notificacionesPlan.js): la app reprograma cada vez que
//     se abre, así que quien la usa recibe todos y a quien no la abre no se le
//     acumulan.
//   - Nunca en horario de turno, si la persona registró su calendario.
//   - Es la app instalada: sin precio, sin "suscríbete", sin mipae.cl
//     (venta-solo-web). La membresía se nombra solo cuando su prueba está en
//     juego, igual que en la hoja, y en iPhone nunca (`sinPrueba`).
//   - Con una membresía vigente o la prueba en curso no hay nada que
//     invitarle: no recibe estos avisos.
//   - Lo que dice de la prueba es lo que se puede comprobar al programarlo.
//     El aviso del 25 que dice "ya puedes probar" (uno, el primero que queda
//     ese día) solo se programa si al programarlo la prueba ya se puede
//     activar, o sea, si Sebastián ya abrió el 25 y la persona abrió la app
//     después; si no, ese día lleva avisos de descubrir.
//   - Tocar el aviso abre "Lo nuevo de MiPAE" (extra.destino). Jamás inicia
//     la prueba.
import { calcularTurnoDelDia, overrideDelDia } from '../utils/calendario';
import { segmentoInvitacion, pruebaPara, accesoActual, antesDelInicio, INICIO_MEMBRESIAS } from './invitacionMembresias';
import { accesoPlusAbierto } from './planGating';

// 21-oct 00:00 y 26-oct 00:00 de Chile (horario de verano, UTC-3).
export const CAMPANA_DESDE = '2026-10-21T03:00:00Z';
export const CAMPANA_HASTA = '2026-10-26T03:00:00Z';
export const HORAS_CAMPANA = [10, 18];
export const MAX_EN_COLA = 2;
// Holgura alrededor del turno: ni justo antes de entrar ni justo al salir.
const HOLGURA_TURNO_MS = 30 * 60 * 1000;

const DIA_MS = 24 * 60 * 60 * 1000;
const T_DESDE = Date.parse(CAMPANA_DESDE);
const T_HASTA = Date.parse(CAMPANA_HASTA);
const T_INICIO = Date.parse(INICIO_MEMBRESIAS);

export function enVentanaCampana(fecha) {
  const t = +fecha;
  return t >= T_DESDE && t < T_HASTA;
}

// Para el texto de Ajustes: ¿hay campaña hoy o más adelante?
export function campanaVigente(ahora = Date.now()) {
  return +ahora < T_HASTA;
}

// Lo que dice cada aviso de descubrir: lo concreto que la persona ya puede
// hacer en su experiencia, con las mismas ideas de la hoja
// (invitacionMembresias.js). Ninguno nombra la membresía.
export const DESCUBRE = {
  estudiante: [
    { title: 'Del caso a tu PAE', body: 'Organiza la valoración con el modelo de tu ramo y arma tu PAE con el fundamento de cada cuidado.' },
    { title: 'Lo nuevo de MiPAE Estudiante', body: 'Mira con un ejemplo lo que ya puedes hacer en tu experiencia.' },
    { title: 'Repasa a tu ritmo', body: 'El reto del día explica cada respuesta, y el modo estudio te espera cuando quieras.' },
  ],
  enfermero: [
    { title: 'Dicta tu turno', body: 'MiPAE en turno ordena por sistemas lo que vas registrando. Tú revisas la evolución antes de copiarla.' },
    { title: 'Lo nuevo de MiPAE Enfermero', body: 'Mira con un ejemplo cómo queda una evolución dictada, lista para revisar.' },
    { title: 'Antes de copiar, revisa', body: 'Tu evolución queda como borrador para que la corrijas y la copies a la ficha cuando esté lista.' },
  ],
  tens: [
    { title: 'Tus cuidados, de un toque', body: 'Ves qué toca con cada paciente y lo registras desde la lista.' },
    { title: 'Lo nuevo de MiPAE TENS', body: 'MiPAE TENS es gratuito en esta etapa. Mira lo que ya puedes hacer en tu turno.' },
    { title: 'Entrega tu turno ordenado', body: 'Lo importante de cada paciente queda a la vista para entregar tu turno.' },
  ],
};

const EN_JUEGO = new Set(['disponible', 'desde_inicio']);

// El 24, a quien la prueba le suma algo desde el 25.
function avisoVispera(prueba) {
  return {
    title: 'Mañana parte lo nuevo de MiPAE',
    body: `Conoce las herramientas de tu experiencia y revisa tu acceso en Mi perfil.`,
  };
}

// El 25, solo si al programarlo la prueba ya se puede activar.
function avisoPrueba(prueba) {
  return {
    title: 'Conoce lo nuevo de MiPAE',
    body: `Mira lo nuevo de tu experiencia. Lo que creaste sigue disponible.`,
  };
}

function medianoche(base, sumaDias) {
  const d = new Date(base);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + sumaDias);
  return d;
}

function aHora(base, hhmm) {
  const [h, m] = String(hhmm).split(':').map(Number);
  const d = new Date(base);
  d.setHours(h, m || 0, 0, 0);
  return d;
}

// Jitter determinístico de −20..+19 min: no se siente robótico y es estable
// si se reprograma el mismo día.
function jitter(seed) {
  const x = Math.sin(seed * 7919) * 10000;
  return Math.floor((x - Math.floor(x)) * 40) - 20;
}

// ¿Cae dentro de un turno (o pegado a él) según el calendario registrado,
// con los cambios hechos a mano (`turno_overrides`)? Mira el día del aviso y
// el anterior, por los turnos que cruzan la noche.
export function enTurno(fecha, rotacion, overrides = []) {
  if (!rotacion?.patron) return false;
  const t = +fecha;
  for (const k of [-1, 0]) {
    const dia = medianoche(fecha, k);
    const turno = calcularTurnoDelDia(dia, rotacion, overrideDelDia(dia, overrides || []));
    if (!turno || turno.tipo === 'libre' || !turno.horaInicio || !turno.horaFin) continue;
    const ini = aHora(dia, turno.horaInicio).getTime();
    const fin = aHora(turno.terminaSiguienteDia ? medianoche(dia, 1) : dia, turno.horaFin).getTime();
    if (t >= ini - HOLGURA_TURNO_MS && t <= fin + HOLGURA_TURNO_MS) return true;
  }
  return false;
}

// ¿A quién le corresponde la campaña? Solo con su experiencia elegida y sin
// una membresía o prueba en curso.
export function recibeCampana(profile, ahora = Date.now()) {
  if (!profile) return false;
  const segmento = segmentoInvitacion(profile);
  if (!DESCUBRE[segmento]) return false;
  if (accesoActual(profile, ahora).tipo !== 'gratis') return false;
  return pruebaPara(profile, ahora).estado !== 'en_curso';
}

/**
 * Los próximos avisos de la campaña, como mucho MAX_EN_COLA.
 * Devuelve { activa, plan: [{ at, title, body, extra }] }; el aviso de la
 * prueba lleva además `prueba: true`. `activa` dice si a
 * esta persona le corresponde la campaña en lo que queda de ventana (y por
 * tanto si los tips de esos días se reemplazan).
 */
export function planLanzamiento({ ahora = new Date(), profile = null, rotacion = null, overrides = [], pruebaAvisada = false, sinPrueba = false, margenMs = 60 * 1000 } = {}) {
  const t0 = +ahora;
  if (t0 >= T_HASTA || !recibeCampana(profile, t0)) return { activa: false, plan: [] };
  const segmento = segmentoInvitacion(profile);
  const prueba = pruebaPara(profile, t0);
  // `sinPrueba` (iPhone, decisión de Sebastián del 20-oct): Apple pide permiso
  // explícito para avisos que promocionan, así que en iPhone el 24 y el 25 no
  // nombran la membresía ni la prueba y van avisos de herramientas. La prueba
  // sigue en la hoja dentro de la app.
  const enJuego = !sinPrueba && (segmento === 'estudiante' || segmento === 'enfermero') && EN_JUEGO.has(prueba.estado);
  // "Ya puedes probar" solo cuando Sebastián ya activó el 25 (cerró el acceso
  // abierto): la activación es suya ese día, también para la estudiante.
  const pruebaYa = !sinPrueba && !antesDelInicio(t0) && !accesoPlusAbierto(t0) && prueba.estado === 'disponible';

  const plan = [];
  // `pruebaAvisada`: el aviso "ya puedes probar" ya llegó en una
  // programación anterior (lo recuerda notifications.js). Se dice una vez.
  let pruebaDicha = pruebaAvisada;
  const dias = Math.ceil((T_HASTA - t0) / DIA_MS) + 1;
  for (let i = 0; i < dias && plan.length < MAX_EN_COLA; i++) {
    const dia = medianoche(ahora, i);
    for (const [slot, hora] of HORAS_CAMPANA.entries()) {
      if (plan.length >= MAX_EN_COLA) break;
      const at = new Date(dia);
      at.setHours(hora, 0, 0, 0);
      const seed = Math.floor(dia.getTime() / DIA_MS) * 2 + slot;
      at.setMinutes(at.getMinutes() + jitter(seed));
      if (at.getTime() <= t0 + margenMs || !enVentanaCampana(at) || enTurno(at, rotacion, overrides)) continue;

      let mensaje;
      const esVispera = at.getTime() >= T_INICIO - DIA_MS && at.getTime() < T_INICIO;
      const es25 = at.getTime() >= T_INICIO;
      let esPrueba = false;
      if (es25 && pruebaYa && !pruebaDicha) { mensaje = avisoPrueba(prueba); pruebaDicha = true; esPrueba = true; }
      else if (esVispera && enJuego && slot === 1) mensaje = avisoVispera(prueba);
      else {
        const lista = DESCUBRE[segmento];
        mensaje = lista[seed % lista.length];
      }
      plan.push({ at, title: mensaje.title, body: mensaje.body, extra: { destino: 'lo-nuevo' }, ...(esPrueba ? { prueba: true } : {}) });
    }
  }
  return { activa: true, plan };
}

